import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { Grid, CircularProgress, Box, Button } from '@mui/material';
import { getSearchMap } from 'api';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { useProfile } from 'context';
import useDebounceMapSearch from 'hooks/debounceMapSearch';
import { useNavigate } from 'react-router-dom';
import SearchBanner from './searchBanner';
import { useMediaQuery } from '@mui/material';
import { getTheme } from 'config/theme';
import SpaceCard from './spaceCard';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import MobileMapOverlay from './mobileMapOverlay';
import LoadingCardPlaceholder from './loadingCardPlaceholder';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const MAP_CONFIG = {
  containerStyle: {
    width: '100%',
    height: ({ isXS }) => isXS ? 'calc(100vh - 72px - 170px)' : 'calc(100vh - 72px)',
  },
  defaultCenter: {
    lat: 51.5074,  // Center of Greater London
    lng: -0.1278,  // Center of Greater London
  },
  options: {
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false
  }
};

const BookingFind = () => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const isLoggedIn = localStorage.getItem('token') ? true : false;
  // Map state for new integration
  const [, setMap] = useState(null);

  const [isMapVisible, setIsMapVisible] = useState(false);

  const mapRef = useRef(null);
  const isManualPan = useRef(false);
  const listItemRefs = useRef({});
  const listContainerRef = useRef(null);
  const swiperRef = useRef(null);

  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [coworkingSpaces, setCoworkingSpaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [coordinates, setCoordinates] = useState(MAP_CONFIG.defaultCenter);
  const [zoom, setZoom] = useState(10);  // Zoom level 10 shows Greater London area
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);
  const [sortBy, setSortBy] = useState('price');
  const [startDate, setStartDate] = useState(dayjs().startOf('week'));
  const endDate = startDate.add(4, 'day');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const lastCoordinates = useRef(coordinates);
  const debouncedCoordinates = useDebounceMapSearch(coordinates, 300);
  const debouncedZoom = useDebounceMapSearch(zoom, 300); // 300
  const { spaces } = useProfile();
  const navigate = useNavigate();
  const isXS = useMediaQuery('(max-width:900px)');
  const [theme] = useState(getTheme(null));
  const [selectedArea, setSelectedArea] = useState('shoreditch');
  const [selectedService, setSelectedService] = useState('HD');
  const [priceDisplayMode, setPriceDisplayMode] = useState('week'); // 'best' | 'week'

  // Sort coworking spaces based on sortBy and selectedDayIndex
  const sortedCoworkingSpaces = useMemo(() => {
    if (!coworkingSpaces || coworkingSpaces.length === 0) return [];
    
    const sorted = [...coworkingSpaces];
    
    if (sortBy === 'price') {
      sorted.sort((a, b) => {
        // Get price for comparison
        let priceA, priceB;
        
        if (selectedDayIndex !== null && selectedDayIndex !== undefined) {
          // Sort by selected day's price
          priceA = a.item_price?.[selectedDayIndex] ?? Infinity;
          priceB = b.item_price?.[selectedDayIndex] ?? Infinity;
        } else {
          // Sort by best (lowest) price of the week
          const validPricesA = (a.item_price || []).filter(p => typeof p === 'number');
          const validPricesB = (b.item_price || []).filter(p => typeof p === 'number');
          priceA = validPricesA.length > 0 ? Math.min(...validPricesA) : Infinity;
          priceB = validPricesB.length > 0 ? Math.min(...validPricesB) : Infinity;
        }
        
        return priceA - priceB;
      });
    } else if (sortBy === 'distance') {
      sorted.sort((a, b) => {
        // Assuming venue_location is [lng, lat] and we compare to current coordinates
        const distA = a.distance ?? Infinity;
        const distB = b.distance ?? Infinity;
        return distA - distB;
      });
    }
    
    return sorted;
  }, [coworkingSpaces, sortBy, selectedDayIndex]);

  const handlePrevWeek = () => {
    setStartDate(prevStartDate => prevStartDate.add(-7, 'day'));
  };

  const handleNextWeek = () => {
    setStartDate(prevStartDate => prevStartDate.add(7, 'day'));
  };

  const getRadiusFromZoom = useCallback((zoomLevel) => {
    const metersPerPixel = 156543.03392 * Math.cos(coordinates.lat * Math.PI / 180) / Math.pow(2, zoomLevel);
    const mapHeight = window.innerHeight;
    return (metersPerPixel * mapHeight) / 2;
  }, [coordinates.lat]);

  const scrollToListItem = (slug) => {
    if (listItemRefs?.current[slug] && listContainerRef?.current) {
      const item = listItemRefs.current[slug];
      const container = listContainerRef.current;
      container.scrollTo({
        top: item.offsetTop - container.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const getMinimumPrice = (itemPrices) => {
    if (!itemPrices || itemPrices.length === 0) return null;
    return Math.min(...itemPrices.filter(price => typeof price === 'number' && !isNaN(price)));
  };

  const moveMapToLocation = (location) => {
    if (mapRef.current) {
      mapRef.current.panTo(location);
    }
  };

  const moveToSlide = (slug) => {
    const slideIndex = coworkingSpaces?.findIndex((space) => space.slug === slug);
    if (slideIndex !== -1 && swiperRef.current) {
      swiperRef.current.slideTo(slideIndex, 500);
    }
  };

  const dateClicked = (space, date) => {
    setSelectedBooking({ space, date });
  };



  const getMapWidth = () => {
    // Get window width
    const windowWidth = window.innerWidth;
    // Calculate 40% of window width
    const percentageWidth = windowWidth * 0.35;

    // If 40% is less than 400px, return 400px, otherwise return 40%
    if (percentageWidth < 400) {
      return '400px';
    }
    return '35%';
  };
  const [, setMapWidth] = useState(getMapWidth());

  useEffect(() => {
    const handleResize = () => {
      setMapWidth(getMapWidth());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Updated Google Maps callbacks
  const onLoad = useCallback((map) => {
    mapRef.current = map;
    setMap(map);

    map.addListener('idle', () => {
      const center = map.getCenter();
      const newCoords = {
        lat: center.lat(),
        lng: center.lng()
      };

      if (
        Math.abs(newCoords.lat - lastCoordinates.current.lat) > 0.0001 ||
        Math.abs(newCoords.lng - lastCoordinates.current.lng) > 0.0001
      ) {
        lastCoordinates.current = newCoords;
        setCoordinates(newCoords);
      }
    });

    map.addListener('zoom_changed', () => {
      setZoom(map.getZoom());
    });

    setDateRange({
      start: startDate.format('DD MMM'),
      end: endDate.format('DD MMM')
    });
  }, [startDate, endDate]);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
    setMap(null);
  }, []);

  const createMarkerIcon = useCallback((space, isHovered) => {

    // Check if all prices are equal to original prices (no discounts)
    const allOriginalPrices = space.item_price.every((price, index) =>
      price === null || space.original_price[index] === null ||
      price === space.original_price[index]
    );

    const hasMinimumPrice = space.item_price.some((price, index) =>
      price !== null && space.minimum_price[index] !== null &&
      price === space.minimum_price[index]
    ) && !allOriginalPrices;

    // Check if there's at least one discounted price
    const hasDiscountedPrice = space.item_price.some((price, index) =>
      price !== null &&
      space.original_price[index] !== null &&
      space.minimum_price[index] !== null &&
      price < space.original_price[index] &&
      price > space.minimum_price[index]
    );

    // Determine color based on conditions
    let markerColor;
    if (hasMinimumPrice) {
      markerColor = theme.palette.secondary.main;
    } else if (allOriginalPrices) {
      markerColor = theme.palette.blue.sad;
    } else if (hasDiscountedPrice) {
      markerColor = theme.palette.green.sad;
    } else {
      markerColor = theme.palette.blue.sad; // Default fallback
    }



    // Rest of your marker creation code...
    const minPrice = getMinimumPrice(space.item_price);
    const displayPrice = minPrice !== null ? minPrice.toFixed(0) : '-';

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40">
          <circle 
            cx="50"
            cy="50"
            r="40"
            stroke="#CCC"
            stroke-width="5"
            fill="${markerColor}"/>
          <text x="50" y="50" 
            text-anchor="middle" 
            dy=".3em" 
            fill="white" 
            font-size="${isHovered ? '30' : '28'}"
            font-weight="bold"
            font-family="Arial">
            £${displayPrice}
          </text>
        </svg>
      `)}`,
      scaledSize: new window.google.maps.Size(isHovered ? 48 : 40, isHovered ? 48 : 40),
      anchor: new window.google.maps.Point(isHovered ? 24 : 20, isHovered ? 24 : 20),
    };
  }, []);

  useEffect(() => {
    if (selectedSpace || selectedBooking) {
      let bookingURL = "";

      if (selectedSpace) {
        bookingURL = `/venue/${selectedSpace.slug}`;
      } else if (selectedBooking) {
        bookingURL = `/venue/${selectedBooking.space.slug}`;
        const selectedDate = selectedBooking?.date;

        if (selectedDate) {
          const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
          bookingURL += `?date=${formattedDate}`;
        }
      } else {
        return;
      }

      // Add booking type to the URL
      if (selectedService) {
        // If URL already has a query parameter, append with &, otherwise use ?
        bookingURL += bookingURL.includes('?') ? `&type=${selectedService}` : `?type=${selectedService}`;
      }
      
      navigate(bookingURL);
    }
  }, [selectedSpace, selectedBooking, navigate, selectedService]);

  // Fetch coworking spaces
  useEffect(() => {

    const fetchCoworkingSpaces = async () => {
      const radiusInMeters = getRadiusFromZoom(debouncedZoom);
      if (!debouncedCoordinates) return;
      setDateRange({
        start: startDate.format('DD MMM'),
        end: endDate.format('DD MMM')
      });

      try {
        setLoading(true);
        const params = {
          start: startDate.format('YYYY-MM-DD'),
          end: endDate.format('YYYY-MM-DD'),
          spaces,
          latitude: debouncedCoordinates.lat,
          longitude: debouncedCoordinates.lng,
          distance: radiusInMeters,
          area: selectedArea,
          booking_type: selectedService
        };

        const results = await getSearchMap(params);
        setCoworkingSpaces(results);
      } catch (error) {
        console.error('Error fetching coworking spaces:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCoworkingSpaces();
  }, [debouncedCoordinates, debouncedZoom, spaces, startDate, selectedService]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Common Header with Filters */}

      {/* Content Area */}
      <Grid
        item
        sx={{
          transition: 'all 0.3s ease',
          width: isMapVisible ? `calc(100% - ${getMapWidth()})` : '100%',
          //height: 'calc(100vh - 72px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <SearchBanner
          startDate={startDate}
          endDate={endDate}
          handlePrevWeek={handlePrevWeek}
          handleNextWeek={handleNextWeek}
          selectedArea={selectedArea}
          setSelectedArea={setSelectedArea}
          selectedService={selectedService}
          setSelectedService={setSelectedService}
          isLoggedIn={isLoggedIn}
          coworkingSpaces={coworkingSpaces}
          selectedDayIndex={selectedDayIndex}
          onDaySelect={setSelectedDayIndex}
          sortBy={sortBy}
          setSortBy={setSortBy}
          priceDisplayMode={priceDisplayMode}
          setPriceDisplayMode={setPriceDisplayMode}
        />




        <Box ref={listContainerRef} sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: { xs: '12px', sm: '20px', md: '24px' },
          backgroundColor: '#f8f9fa',
          height: `calc(100% - 8px)`,
          // Subtle grid background pattern
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}>

          {loading ? (
            <LoadingCardPlaceholder count={8} isMapVisible={isMapVisible} />
          ) : (
            <Grid
              container
              spacing={3}
              columns={{ xs: 12, md: 12, xl: 12 }}
            >
              {sortedCoworkingSpaces?.map((space) => (
                <Grid
                  item
                  xs={12}
                  md={isMapVisible ? 6 : 4}
                  xl={isMapVisible ? 6 : 3}
                  key={space.slug}
                  sx={{
                    minWidth: '300px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <SpaceCard
                    space={space}
                    selectedDayIndex={selectedDayIndex}
                    setSelectedSpace={setSelectedSpace}
                    startDate={startDate}
                    endDate={endDate}
                    priceDisplayMode={priceDisplayMode}
                    onDetailClick={(space, date) => dateClicked(space, date)}
                    onMouseEnter={() => setHoveredMarkerId(space.slug)}
                    onMouseLeave={() => setHoveredMarkerId(null)}
                    listItemRefs={listItemRefs}
                    isHovered={hoveredMarkerId === space.slug}
                    isSelected={selectedSpace?.slug === space.slug}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Grid>


      {/* Main Content (Cards) */}

      {/* Map Panel */}
      {!isXS && isMapVisible && (

        // Desktop Sliding Map Panel
        <Box
          sx={{
            position: 'fixed',
            top: 72,
            right: 0,
            width: getMapWidth(),
            height: 'calc(100vh - 72px)',
            transform: isMapVisible ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.3s ease',
            backgroundColor: 'background.paper',
            boxShadow: '-4px 0 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
          }}
        >
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: '100%',
              }}
              center={coordinates}
              zoom={zoom}
              options={MAP_CONFIG.options}
              onLoad={onLoad}
              onUnmount={onUnmount}
            >
              {coworkingSpaces?.map(space => (
                <Marker
                  key={space.slug}
                  position={{
                    lat: space.venue_location[1],
                    lng: space.venue_location[0]
                  }}
                  icon={createMarkerIcon(space, space.slug === hoveredMarkerId)}
                  onClick={() => {
                    scrollToListItem(space.slug);
                    moveToSlide(space.slug);
                  }}
                  onMouseOver={() => setHoveredMarkerId(space.slug)}
                  onMouseOut={() => setHoveredMarkerId(null)}
                />
              ))}
            </GoogleMap>
          ) : (
            <CircularProgress />
          )}
        </Box>

      )}




      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1100,
        }}
      >
        <Button
          variant="contained"
          startIcon={isMapVisible ? <LocationOffIcon /> : <LocationOnIcon />}
          onClick={() => setIsMapVisible(!isMapVisible)}
          sx={{
            borderRadius: '28px',
            padding: '10px 24px',
            backgroundColor: 'white',
            color: 'text.primary',
            fontWeight: 600,
            fontSize: '0.9rem',
            textTransform: 'none',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid rgba(0,0,0,0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'white',
              boxShadow: '0 6px 28px rgba(0,0,0,0.2)',
              transform: 'translateY(-2px)',
            }
          }}
        >
          {isMapVisible ? 'Hide Map' : 'Show Map'}
        </Button>
      </Box>
      {isXS && isMapVisible && <MobileMapOverlay
        coworkingSpaces={coworkingSpaces}
        startDate={startDate}
        endDate={endDate}
        coordinates={coordinates}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        setSelectedSpace={setSelectedSpace}
        hoveredMarkerId={hoveredMarkerId}
        setHoveredMarkerId={setHoveredMarkerId}
        isManualPan={isManualPan}
        listItemRefs={listItemRefs}
        moveToSlide={moveToSlide}
        moveMapToLocation={moveMapToLocation}
        swiperRef={swiperRef}
        setIsMapVisible={setIsMapVisible}
        setSelectedSpaceId={setSelectedSpace}
        dateClicked={dateClicked}
        createMarkerIcon={createMarkerIcon}
        scrollToListItem={scrollToListItem}
      />}
    </Box>
  );
};

export default React.memo(BookingFind);